import React from 'react'
import { makeStyles, Tooltip } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import {
  biteClassificationFields,
  biteClassificationValues,
  isCompleteBiteClassification
} from 'utils/biteClassificationUtils'
import BiteClassificationExplanation from './BiteClassificationExplanation'
import biteClassIcon from './artwork/bite-class-icon.png'

const colors = { I: '#61B746', II: '#793CF1', III: '#EE944E', uncertain: '#7B8290' }
const useStyles = makeStyles({
  root: { fontFamily: 'Dazzed, sans-serif', fontSize: 14, lineHeight: '20px', minWidth: 0 },
  // Aligns with the condition rows below it
  tagging: { paddingLeft: 8 },
  copilot: { display: 'flex', alignItems: 'center', gap: 12 },
  // Left wraps under Right in a narrow Copilot column
  sides: { display: 'flex', flexWrap: 'wrap', gap: '8px 12px', minWidth: 0 },
  icon: { flexShrink: 0, width: 24, height: 20, objectFit: 'contain' },
  copilotTitle: { display: 'flex', alignItems: 'center', flexShrink: 0, gap: 8 },
  side: { display: 'flex', flexDirection: 'column', gap: 4 },
  sideLabel: { color: 'var(--text-color-11)', fontWeight: 500 },
  sideValues: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 8px' },
  sideValue: { display: 'flex', alignItems: 'center', gap: 6 },
  matrix: {
    borderSpacing: '12px 6px',
    margin: '-6px -12px',
    '& th': { fontWeight: 400 },
    '& tbody th': { textAlign: 'right' },
    '& td': { textAlign: 'center' }
  },
  title: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontWeight: 500 },
  scanHeading: { marginBottom: 0, fontWeight: 600 },
  // Narrow enough that "Last Bite Class" wraps to two lines at 14px, as in the design
  copilotHeading: {
    display: 'block',
    maxWidth: 64,
    marginBottom: 0,
    fontWeight: 400,
    '& button': { marginLeft: 6, verticalAlign: 'middle' }
  },
  help: {
    border: '1px solid #D3D5DE',
    borderRadius: '50%',
    width: 16,
    height: 16,
    color: '#747B8D',
    background: 'white',
    padding: 0,
    fontSize: 11,
    lineHeight: '14px',
    cursor: 'help'
  },
  tooltip: {
    background: 'white',
    color: '#111',
    maxWidth: 'min(560px, calc(100vw - 80px))',
    maxHeight: 'calc(100vh - 64px)',
    overflowY: 'auto',
    boxSizing: 'border-box',
    padding: 16,
    boxShadow: '0 3px 16px #0003',
    borderRadius: 16
  },
  // Left wraps under Right when the panel is narrow (the Scan Summary side panel is 280px)
  fields: { display: 'flex', flexWrap: 'wrap', gap: '12px 24px' },
  // Label and choices are separate cells so the choices line up within a side
  fieldSide: { display: 'grid', gridTemplateColumns: 'max-content max-content', alignItems: 'center', gap: 8 },
  fieldLabel: { fontWeight: 500 },
  choices: { display: 'flex', gap: 6 },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 20,
    height: 20,
    borderRadius: '50%',
    fontFamily: 'inherit',
    fontSize: 12,
    border: '1px solid var(--text-color-25)',
    color: 'var(--text-color-25)',
    background: 'white',
    padding: 0,
    '&:focus-visible': { outline: '2px solid #27205E', outlineOffset: 3 }
  },
  required: { color: 'var(--text-color-11)', fontSize: 12, marginTop: 8 }
})

const BiteClassification = ({ value = {}, onChange, title, variant = 'tagging' }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const editable = !!onChange
  if (!editable && !isCompleteBiteClassification(value)) return null

  const valueLabel = option => t(`biteClassification.values.${option}`)
  const badge = field => (
    <span
      className={classes.badge}
      title={valueLabel(value[field])}
      aria-label={`${t(`biteClassification.fields.${field}`)}: ${valueLabel(value[field])}`}
      style={{ background: colors[value[field]], borderColor: colors[value[field]], color: 'white' }}
    >
      {value[field] === 'uncertain' ? '?' : value[field]}
    </span>
  )
  const heading = className => (
    <div className={[classes.title, className].filter(Boolean).join(' ')}>
      {title || t('biteClassification.title')}
      <Tooltip
        title={<BiteClassificationExplanation />}
        classes={{ tooltip: classes.tooltip }}
        placement="left"
        PopperProps={{
          popperOptions: { positionFixed: true },
          modifiers: { preventOverflow: { boundariesElement: 'viewport', padding: 16 } }
        }}
        interactive
      >
        <button type="button" className={classes.help} aria-label={t('biteClassification.explanation')}>
          ?
        </button>
      </Tooltip>
    </div>
  )
  if (!editable && variant === 'scan')
    return (
      <section className={classes.root} aria-label={title || t('biteClassification.title')}>
        <table className={classes.matrix}>
          <thead>
            <tr>
              <th>{heading(classes.scanHeading)}</th>
              <th scope="col">{t('biteClassification.right')}</th>
              <th scope="col">{t('biteClassification.left')}</th>
            </tr>
          </thead>
          <tbody>
            {['molar', 'canine'].map(tooth => (
              <tr key={tooth}>
                <th scope="row">{t(`biteClassification.${tooth}`)}</th>
                {['right', 'left'].map(side => (
                  <td key={side}>{badge(`${side}${tooth[0].toUpperCase()}${tooth.slice(1)}`)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    )
  if (!editable && variant === 'copilot')
    return (
      <section className={`${classes.root} ${classes.copilot}`} aria-label={title || t('biteClassification.title')}>
        <div className={classes.copilotTitle}>
          <img src={biteClassIcon} className={classes.icon} alt="" />
          {heading(classes.copilotHeading)}
        </div>
        <div className={classes.sides}>
          {['right', 'left'].map(side => (
            <div className={classes.side} key={side}>
              <span className={classes.sideLabel}>{t(`biteClassification.${side}`)}</span>
              <div className={classes.sideValues}>
                {['molar', 'canine'].map(tooth => (
                  <div className={classes.sideValue} key={tooth}>
                    <span>{t(`biteClassification.${tooth}`)}</span>
                    {badge(`${side}${tooth[0].toUpperCase()}${tooth.slice(1)}`)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    )
  return (
    <section className={`${classes.root} ${classes.tagging}`} aria-label={title || t('biteClassification.title')}>
      {heading()}
      <div className={classes.fields}>
        {['right', 'left'].map(side => (
          <div key={side} className={classes.fieldSide}>
            {biteClassificationFields
              .filter(field => field.startsWith(side))
              .map(field => (
                <React.Fragment key={field}>
                  <span className={classes.fieldLabel}>{t(`biteClassification.fields.${field}`)}</span>
                  {editable ? (
                    <div className={classes.choices} role="group" aria-label={t(`biteClassification.fields.${field}`)}>
                      {biteClassificationValues.map(option => (
                        <button
                          type="button"
                          key={option}
                          className={classes.badge}
                          aria-label={`${t(`biteClassification.fields.${field}`)}: ${valueLabel(option)}`}
                          aria-pressed={value[field] === option}
                          title={valueLabel(option)}
                          onClick={() => onChange({ ...value, [field]: option })}
                          style={
                            value[field] === option
                              ? { background: colors[option], borderColor: colors[option], color: 'white' }
                              : { cursor: 'pointer' }
                          }
                        >
                          {option === 'uncertain' ? '?' : option}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span
                      className={classes.badge}
                      title={valueLabel(value[field])}
                      aria-label={valueLabel(value[field])}
                      style={{ background: colors[value[field]], borderColor: colors[value[field]], color: 'white' }}
                    >
                      {value[field] === 'uncertain' ? '?' : value[field]}
                    </span>
                  )}
                </React.Fragment>
              ))}
          </div>
        ))}
      </div>
      {editable && <div className={classes.required}>{t('biteClassification.required')}</div>}
    </section>
  )
}

export default BiteClassification
